/**
 * The rule of the training spots (data/trainingSpots.ts): how much faster cultivation builds in one,
 * for whom, and what holds it back. The World asks this every step (world.ts) and the client reads
 * the World's `spotState()` for its banner and HUD; both always agree.
 */
import { ELEMENT_INFO } from './data/elements';
import { NEIGONG } from './data/neigong';
import { SCHOOLS } from './data/schools';
import { MARTIAL_ARTS } from './data/skills';
import { spotAt, spotsIn, TRAINING_SPOT, type TrainingSpotDef } from './data/trainingSpots';
import type { GameMap } from './map';
import { coreOf } from './region/core';
import type { PlayerProgress } from './progress';

/** The player stepped into a training spot (`id`), out of one (`from`), or from one to another. */
export interface TrainingSpotEvent {
  type: 'trainingSpot';
  id: string | null;
  from: string | null;
}

/** Where the player trains now: the spot, its multiplier for them, and why it is held back. */
export interface TrainingSpotState {
  readonly spot: TrainingSpotDef;
  /** Cultivation × this (1 while paused). */
  readonly mult: number;
  /** The place suits the player (its affinity applies). */
  readonly suits: boolean;
  /** Why the bonus is held back now (fighting, riding …), or null. */
  readonly paused: string | null;
}

type Trainer = Pick<PlayerProgress, 'activeNeigong' | 'school' | 'activeArt'>;

/** True when the spot's affinity applies to this player now. */
export function spotSuits(spot: TrainingSpotDef, pr: Trainer, meditating: boolean): boolean {
  const a = spot.affinity;
  if (!a) return false;
  if (a.meditation) return meditating;
  if (a.element) return NEIGONG[pr.activeNeigong]?.element === a.element;
  if (a.school) return pr.school === a.school;
  if (a.art) return pr.activeArt === a.art;
  return false;
}

/** The spot's cultivation multiplier for this player (never above `TRAINING_SPOT.maxMult`). */
export function spotMult(spot: TrainingSpotDef, pr: Trainer, meditating: boolean): number {
  const m = spotSuits(spot, pr, meditating) ? spot.affinity!.mult : spot.mult;
  return Math.min(TRAINING_SPOT.maxMult, Math.max(1, m));
}

/** Whom the place favours, in Thai ("กำลังภายในธาตุอ่อน"), or null. */
export function spotAffinityLabel(spot: TrainingSpotDef): string | null {
  const a = spot.affinity;
  if (!a) return null;
  if (a.meditation) return 'นั่งสมาธิ';
  if (a.element) return `กำลังภายในธาตุ${ELEMENT_INFO[a.element].name}`;
  if (a.school) return `ศิษย์${SCHOOLS[a.school]?.name ?? a.school}`;
  if (a.art) return `ผู้ใช้${MARTIAL_ARTS[a.art]?.name ?? a.art}`;
  return null;
}

/** "×1.5", "×1.75", "×2". */
export const fmtMult = (m: number): string => `×${Number(m.toFixed(2))}`;

/**
 * The spot a point of map `m` lies in, or null. The data's circles are in the authored (village)
 * map's coordinates; on a region map they move by its core offset (region/core.ts).
 */
export function spotOn(m: GameMap, x: number, y: number): TrainingSpotDef | null {
  const o = coreOf(m);
  return spotAt(m.zone, x - o.x, y - o.y);
}

/** The training spots of map `m`, their circles in `m`'s coordinates (moved by the core offset). */
export function spotsOn(m: GameMap): readonly TrainingSpotDef[] {
  const o = coreOf(m);
  const list = spotsIn(m.zone);
  if (!o.x && !o.y) return list;
  return list.map((s) => ({ ...s, area: { ...s.area, x: s.area.x + o.x, y: s.area.y + o.y } }));
}
